import Vue from 'vue'
import axios from 'axios'
import URLSearchParams from 'url-search-params'

export const MoqoApi = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
})

MoqoApi.savedConfig = null
MoqoApi.teamIdForSignup = null
MoqoApi.eventBus = new Vue()

MoqoApi.getEcp = (teamsiteToken) => {
  return MoqoApi.get(`team_sites/${teamsiteToken}`)
}

MoqoApi.normalizeConfig = (config) => {
  if (!config.hero) {
    config.hero = {}
  }

  config.intro = {
    title: config.intro_headline,
    body: config.intro,
    car_image: config.hero.cars_image
  }

  config.hero = {
    title: config.hero.slogan,
    subtitle: config.hero.overline,
    image: config.hero.hero_image
  }

  if (config.hero.title) {
    config.hero.title = config.hero.title.replace(/<br>/g, ' ').replace(/<br\/>/g, ' ')
  }

  if (!config.team_context) {
    if (config.app && config.app.name != 'fleetbutler' && config.app.name.indexOf('Moqo') < 0) {
      config.team_context = 'plus'
    } else {
      config.team_context = 'work'
    }
  }

  return config
}

MoqoApi.setBackendEnv = (env) => {
  MoqoApi.defaults.baseURL = {
    release: 'https://portal.moqo.de',
    prerelease: 'https://prerelease.moqo.de',
    development: 'https://development.moqo.de',
    localhost: 'http://localhost:3000'
  }[env]

  MoqoApi.eventBus.$emit('set:env', env)
}

MoqoApi.refreshEcpConfig = (teamsiteToken) => {
  if (!MoqoApi.defaults.baseURL) {
    return
  }
  return MoqoApi.getEcp(teamsiteToken).then(response => {
    let responseJson = MoqoApi.normalizeConfig(response.data.team)
    // Fast-fix: Avoid showing unconfigured teamsites

    if (responseJson.client_redirect) {
      location.href = responseJson.client_redirect
      return
    }

    if (!responseJson.color) {
      MoqoApi.savedConfig = null
      MoqoApi.eventBus.$emit('teamsite-config:receive', null)
      MoqoApi.eventBus.$emit('teamsite-config:error', { status: 404 })
    } else {
      MoqoApi.savedConfig = responseJson
      MoqoApi.eventBus.$emit('teamsite-config:receive', MoqoApi.savedConfig)
    }
  })
  .catch(error => {
    MoqoApi.savedConfig = null
    MoqoApi.eventBus.$emit('teamsite-config:receive', null)
    MoqoApi.eventBus.$emit('teamsite-config:error', { status: error.response.status })
  })
}

Vue.mixin({

  /**
   * EVENT HANDLING
   */

  created () {
    MoqoApi.eventBus.$on('teamsite-config:receive', data => {
      this.teamsiteConfig = data
      if (data) {
        this.httpError = null
      }
    })

    MoqoApi.eventBus.$on('teamsite-config:error', error => {
      this.httpError = error
    })

    MoqoApi.eventBus.$on('set:env', data => {
      this.backendHost = MoqoApi.defaults.baseURL
    })
  },

  mounted () {
    this.$bus.$on('set:team-for-signup', (teamId) => {
      MoqoApi.teamIdForSignup = teamId
      this.teamIdForSignup = teamId
    })
  },

  data () {
    return {
      // Provide the already fetched data as a default
      teamsiteConfig: MoqoApi.savedConfig,
      backendHost: MoqoApi.defaults.baseURL,
      httpError: null,
      teamIdForSignup: MoqoApi.teamIdForSignup
    }
  },


  /**
   * Commonly used properties
   */
  computed: {
    /**
     * Returns common key value pairs from the teamsiteConfig that might be used during string interpolations
     */
    i18nParams () {
      return {
        app_name: this.teamsiteConfig.app && this.teamsiteConfig.app.name,
        team_name: this.teamsiteConfig.name,
        team_context: MoqoApi.savedConfig.team_context
      }
    },

    previewMode () {
      return !!process.previewMode
    },

    isPoweredByMoqo () {
      return this.teamsiteConfig.team_context == 'work'
    },

    poweredByImageName () {
      return `enabled_by_moqo`
    },

    targetHost() {
      // If the backend returns a custom host we will use that for URLs, default backend host otherwise
      if (MoqoApi.savedConfig && MoqoApi.savedConfig.target_host) {
        return MoqoApi.savedConfig.target_host
      }
      return this.backendHost
    },

    routes () {
      let teamId = ''
      let locale = ''
      if (this.teamsiteConfig) {
        teamId = this.teamsiteConfig.id
        locale = this.teamsiteConfig.locale
      }

      return {
        signUpUrl: this.signUpSrc(),
        signInUrl: `${this.targetHost}/login?team_id=${teamId}&locale=${locale}&ref=ecp`,
        siteNoticeUrl: `${this.targetHost}/site_notice?team_id=${teamId}&locale=${locale}`,
        helpCenterUrl: `${this.targetHost}/contact?team_id=${teamId}&locale=${locale}`,
        privacyPolicyUrl: `${this.targetHost}/privacy_policy?team_id=${teamId}&locale=${locale}`
      }
    },

    moqoColor () {
      if (this.teamsiteConfig.team_context == 'work') { return '#7ADEF4' }
      if (this.teamsiteConfig.color) { return this.teamsiteConfig.color }
      return '#202020'
    },

    clientLanguage () {
      let clientLanguage

      if (navigator.userLanguage) { // IE10, IE11
        clientLanguage = navigator.userLanguage
      } else if (navigator.language) { // FF, Chrome, Safari, IE11
        clientLanguage = navigator.language
      } else {
        clientLanguage = 'de'
      }

      if (clientLanguage.indexOf('-') !== -1) { // for 'de-DE' strings
        clientLanguage = clientLanguage.substring(0, clientLanguage.indexOf('-'))
      }

      return clientLanguage
    }

  },

  /**
   * Helper methods for interpolations
   */
  methods: {
    signUpSrc (url, params) {
      if (!this.teamsiteConfig) return;
      if (!url) url = this.teamsiteConfig.signup_url;
      if (!params) params = {ref: 'ecp', locale: this.teamsiteConfig.locale};

      let urlAdditionsString = ''

      if (typeof params !== 'undefined') {
        let urlParams = new URLSearchParams()
        for (let key in params) {
          if (params.hasOwnProperty(key)) {
            urlParams.append(key, params[key])
          }
        }
        urlAdditionsString += '/?' + urlParams.toString()
      }

      return `${url}${urlAdditionsString}`
    },
    interpolate (string) {
      if (!string) { return string }
      var s = string + '' // clone
      for(var key of Object.keys(this.i18nParams)) {
        s = s.replace(`{${key}}`, this.i18nParams[key])
      }
      return s
    },

    signup_fee_text(pricing) {
      if(pricing.subscription_costs && pricing.subscription_recurrence != 'subscriptions_unnecessary') {
        return this.$t(`fees.subscription.${pricing.subscription_recurrence}`, {price: pricing.subscription_costs.formatted})
      } else {
        return this.$t('fees.subscription.free')
      }
    },

    subscription_fee_text(pricing) {
      if (pricing.signup_fee) {
        return this.$t('fees.signup.with_charge', {price: pricing.signup_fee.formatted})
      } else {
        return this.$t('fees.signup.free')
      }
    }

  }
})



// WEBPACK FOOTER //
// ./src/lib/MoqoApi.js